import { ReactNode } from 'react';
import { Box, BoxProps } from '../Box/Box';

import { HeadingProps } from '../Heading/Heading';
import { TextProps } from '../Text/Text';

import { skeletonItemStyle, skeletonWrapperStyle } from './Skeleton.css';

interface SkeletonProps extends Pick<BoxProps, 'borderRadius'> {
  rounded?: boolean;
  height: string | number;
  width: string | number;
  tone?: 'white' | 'grey';
}

interface SkeletonTextProps extends Omit<SkeletonProps, 'height'> {
  size: TextProps['size'];
}

interface SkeletonHeadingProps extends Omit<SkeletonProps, 'height'> {
  level: HeadingProps['level'];
}

const textHeightLookup: { [key: string]: number } = {
  xsmall: 8.74,
  small: 10.244444,
  medium: 11.59,
  large: 12.899999,
};

const headingHeightLookup: { [key: string]: number } = {
  1: 28.2,
  2: 25.65,
  3: 22.41,
  4: 20.02,
  5: 16.37,
  6: 14.16,
};

/** ### Skeleton
 *
 * Use this to construct arbitrary loading placeholders
 */
export const Skeleton = ({
  height,
  width,
  rounded = true,
  tone = 'grey',
  ...restProps
}: SkeletonProps): JSX.Element => {
  return <Box className={skeletonItemStyle({ rounded, tone })} style={{ height, width }} {...restProps} />;
};

/** ### SkeletonText
 *
 * A convenient loading placeholder for `<Text>` components from ui-lib.
 *
 * Takes the `size` prop from `<Text>`
 */
export const SkeletonText = ({ size, width, tone = 'grey' }: SkeletonTextProps): JSX.Element => (
  <Skeleton height={textHeightLookup[size || 'medium']} width={width} tone={tone} />
);

/** ### SkeletonHeading
 *
 * A convenient loading placeholder for `<Heading>` components from ui-lib.
 *
 * Takes the `level` prop from `<Heading>`
 */
export const SkeletonHeading = ({ level, width, tone = 'grey' }: SkeletonHeadingProps): JSX.Element => (
  <Skeleton height={headingHeightLookup[level || 1]} width={width} tone={tone} />
);

/** ### SkeletonWithAnimation
 *
 * Used to provide a shimmer animation to a set of `Skeleton` components.
 *
 * Usage: Wrap an arbitrary set of `Skeleton` components with `SkeletonWithAnimation`.
 * The `Skeleton` components can be at an arbitrary depth in the component tree.
 *
 */
export const SkeletonWithAnimation = ({ children }: { children: ReactNode }) => (
  <Box className={skeletonWrapperStyle}>
    <Box width="full">{children}</Box>
  </Box>
);
